import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Disc3, Flame, Trophy, Share2, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { AdSlot } from "@/components/AdSlot";
import { ListenButton } from "@/components/ListenButton";
import { ShareCardModal } from "@/components/ShareCardModal";
import { getQuizState, getTodayResult, recordQuizResult, currentStreak } from "@/lib/quizStorage";
import { track } from "@/lib/analytics";
import { setSeo } from "@/lib/events";

const longDate = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
const verdict = (t, max) => (t === max ? "Perfect. Chart royalty." : t >= max * 0.8 ? "Superb — you know your charts." : t >= max * 0.5 ? "Solid. A true pop historian in the making." : t >= max * 0.2 ? "Not bad — the decades are blurring a little." : "Ouch. Tomorrow is a new chart.");

const StatPill = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="flex items-center gap-2 bg-white border border-[#e5e7eb] rounded-full px-4 h-10">
    <Icon className="w-4 h-4 text-gold" />
    <span className="text-[12px] text-ink-muted">{label}</span>
    <span className="text-[14px] font-bold text-ink">{value}</span>
  </div>
);

const Question = ({ q, idx, total, min, max, onAnswer }) => {
  const [year, setYear] = useState(Math.round((min + max) / 2));
  useEffect(() => { setYear(Math.round((min + max) / 2)); }, [q, min, max]);
  const sport = q.kind === "sport";
  return (
    <div data-testid={`quiz-question-${q.n}`} data-kind={q.kind} className="bg-ink text-white rounded-2xl p-6 md:p-8">
      <p className="text-[11px] uppercase tracking-[0.18em] text-gold font-bold flex items-center gap-2">
        {sport ? <Trophy className="w-3.5 h-3.5" /> : <Disc3 className="w-3.5 h-3.5" />} Question {idx + 1} of {total} · {sport ? "Sporting moment" : "Chart-topper"}
      </p>
      {sport ? (
        <>
          <p className="mt-3 text-[14px] text-white/70">Which year did this happen in the <span data-testid="quiz-competition" className="text-gold font-semibold">{q.competition}</span>?</p>
          <h2 data-testid="quiz-title" className="mt-1 text-2xl md:text-3xl font-bold leading-tight">{q.title}</h2>
        </>
      ) : (
        <>
          <p className="mt-3 text-[14px] text-white/70">Which year did this reach No.1 on the <span data-testid="quiz-chart" className="text-gold font-semibold">{q.chart_label || "UK Singles"}</span> chart?</p>
          <h2 data-testid="quiz-title" className="mt-1 text-2xl md:text-3xl font-bold leading-tight">{q.title}</h2>
          <p data-testid="quiz-artist" className="mt-1 text-[16px] text-white/80">{q.artist}</p>
          <p className="mt-1 text-[12px] text-white/50">{q.weeks} week{q.weeks === 1 ? "" : "s"} at No.1</p>
        </>
      )}
      <div className="mt-6 flex items-center gap-4">
        <input data-testid="quiz-year-slider" type="range" min={min} max={max} value={year} onChange={(e) => setYear(Number(e.target.value))} className="flex-1 accent-[#d4a017]" aria-label="Year" />
        <input data-testid="quiz-year-input" type="number" min={min} max={max} value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24 h-11 rounded-xl bg-white text-ink text-center text-[18px] font-bold outline-none" />
      </div>
      <div className="mt-2 flex justify-between text-[11px] text-white/40"><span>{min}</span><span>{max}</span></div>
      <button type="button" data-testid="quiz-lock-btn" onClick={() => onAnswer(Math.min(max, Math.max(min, year)))} className="mt-6 inline-flex items-center gap-2 px-6 h-12 rounded-full bg-gold text-ink font-semibold hover:bg-white transition-colors">
        Lock in {year} <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

const Results = ({ res, state, onShare }) => (
  <div data-testid="quiz-results">
    <div className="bg-ink text-white rounded-2xl p-6 md:p-8 text-center">
      <p className="text-[11px] uppercase tracking-[0.18em] text-gold font-bold">Today's score</p>
      <p data-testid="quiz-total" className="mt-2 text-6xl font-bold leading-none">{res.total}<span className="text-2xl text-white/50">/{res.max}</span></p>
      <p className="mt-3 text-[15px] text-white/80">{verdict(res.total, res.max)}</p>
      <p className="mt-1 text-[12px] text-white/50">{res.players_today} player{res.players_today === 1 ? "" : "s"} today · average {res.average_today}</p>
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        <button type="button" data-testid="quiz-share-btn" onClick={onShare} className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-gold text-ink text-sm font-semibold hover:bg-white transition-colors"><Share2 className="w-4 h-4" /> Share my score</button>
        <Link to="/number-ones" className="inline-flex items-center px-5 h-11 rounded-full border border-white/30 text-white text-sm font-semibold hover:border-gold hover:text-gold transition-colors">Browse every No.1</Link>
      </div>
    </div>
    <ul className="mt-4 space-y-2">
      {res.results.map((r) => (
        <li key={r.n} data-testid={`quiz-result-${r.n}`} data-kind={r.kind} className="flex items-center gap-3 bg-white border border-[#e5e7eb] rounded-xl px-4 py-3">
          <span className={`w-12 shrink-0 text-center text-[15px] font-bold rounded-lg py-1 ${r.score >= 80 ? "bg-gold text-ink" : r.score >= 40 ? "bg-paper text-ink border border-[#e5e7eb]" : "bg-[#f3f4f6] text-ink-muted"}`}>+{r.score}</span>
          <span className="min-w-0 flex-1">
            {r.kind === "sport" ? (
              <span className="block text-[14px] font-semibold text-ink truncate">{r.title} <span className="text-[10px] uppercase tracking-wider text-gold">{r.competition}</span></span>
            ) : (
              <span className="block text-[14px] font-semibold text-ink truncate">{r.title} <span className="font-normal text-ink-muted">· {r.artist}</span> <span className="text-[10px] uppercase tracking-wider text-gold">{r.chart_label}</span></span>
            )}
            <span className="block text-[12px] text-ink-muted truncate">{r.kind === "sport" ? (r.detail || "") : `No.1 from ${r.start_label}`} · you said {r.guess}{r.diff === 0 ? " — spot on" : ` (${r.diff} yr${r.diff === 1 ? "" : "s"} out)`}</span>
          </span>
          <span className="shrink-0 text-[18px] font-bold text-gold">{r.year}</span>
          {r.kind === "sport" ? (
            <Link to={`/sports?year=${r.year}`} data-testid={`quiz-sport-link-${r.n}`} className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full border border-[#e5e7eb] text-gold hover:border-gold" title="See that sporting year"><Trophy className="w-4 h-4" /></Link>
          ) : (
            <ListenButton artist={r.artist} title={r.title} testid={`quiz-listen-${r.n}`} />
          )}
        </li>
      ))}
    </ul>
    <p className="mt-4 text-[12px] text-ink-muted text-center">Come back tomorrow for four new No.1s and two new sporting moments. Streaks count consecutive days played.</p>
  </div>
);

export default function ChartQuiz() {
  const [quiz, setQuiz] = useState(null);
  const [idx, setIdx] = useState(0);
  const [guesses, setGuesses] = useState([]);
  const [res, setRes] = useState(null);
  const [state, setState] = useState(getQuizState());
  const [shareOpen, setShareOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setSeo({ title: "Daily History Quiz — guess the year of No.1 hits and sporting finals | OddlyTrue", description: "A free daily quiz: guess the year a song hit number one or a champion lifted the trophy. Five questions, a streak to protect and a shareable score card.", canonical: "https://oddlytrue.co/quiz" }); }, []);

  useEffect(() => {
    api.get("/number-ones/quiz").then((r) => {
      setQuiz(r.data);
      const done = getTodayResult(r.data.date);
      if (done) setRes(done);
    }).catch(() => toast.error("Could not load today's quiz"));
  }, []);

  const submit = async (all) => {
    setBusy(true);
    try {
      const r = await api.post("/number-ones/quiz/answer", { date: quiz.date, guesses: all });
      setRes(r.data);
      setState(recordQuizResult(quiz.date, r.data));
      track("chart_quiz_complete", { category: "quiz", total: r.data.total });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not score your answers");
    } finally {
      setBusy(false);
    }
  };

  const answer = (year) => {
    const all = [...guesses, year];
    setGuesses(all);
    if (all.length === quiz.size) submit(all); else setIdx(idx + 1);
  };

  const streak = quiz ? currentStreak(quiz.date) : 0;

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="chart-quiz-page">
        <div className="flex items-center gap-2">
          <Disc3 className="w-4 h-4 text-gold" />
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold">History Quiz - Music & Sports</p>
        </div>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold text-ink leading-tight">Guess the year</h1>
        <p className="mt-3 text-base text-ink-muted max-w-xl">Four No.1s from the UK and US singles and album charts, plus two momentous sporting moments. One question for each: what year? 100 points for the exact year, 80 within one, 60 within three, 40 within five, 20 within ten. {quiz && <span className="text-ink">Puzzle for {longDate(quiz.date)}.</span>}</p>

        <div className="mt-5 flex flex-wrap gap-2">
          <StatPill icon={Flame} label="Streak" value={`${streak} day${streak === 1 ? "" : "s"}`} testid="quiz-streak" />
          <StatPill icon={Trophy} label="Best streak" value={state.best || 0} testid="quiz-best-streak" />
          <StatPill icon={Trophy} label="Best score" value={state.bestScore || 0} testid="quiz-best-score" />
        </div>

        <div className="mt-6">
          {!quiz ? (
            <p className="text-sm text-ink-muted">Spinning the records…</p>
          ) : res ? (
            <Results res={res} state={state} onShare={() => setShareOpen(true)} />
          ) : busy ? (
            <p className="text-sm text-ink-muted">Checking the charts…</p>
          ) : (
            <>
              <div className="flex gap-1.5 mb-3" data-testid="quiz-progress">
                {quiz.questions.map((q, i) => <span key={q.n} className={`h-1.5 flex-1 rounded-full ${i < idx ? "bg-gold" : i === idx ? "bg-ink" : "bg-[#e5e7eb]"}`} />)}
              </div>
              <Question q={quiz.questions[idx]} idx={idx} total={quiz.size} min={quiz.min_year} max={quiz.max_year} onAnswer={answer} />
            </>
          )}
        </div>

        <AdSlot id="adsense-quiz" slot="MID" size="rect300" className="mt-10" />
      </main>
      <Footer />
      {res && quiz && (
        <ShareCardModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          heading="Share your score"
          analyticsName="quiz_card"
          testPrefix="quiz-card"
          shareUrl={`${window.location.origin}/quiz`}
          card={{ badge: "History Quiz - Music & Sports", greeting: `I scored ${res.total}/${res.max}`, title: "Guess the year", body: `${verdict(res.total, res.max)} ${streak > 1 ? `${streak}-day streak.` : ""} Can you beat me? Four No.1s, two sporting finals, one question: what year?`, dateLabel: longDate(quiz.date), footer: "Play today's puzzle → oddlytrue.co/quiz" }}
        />
      )}
    </div>
  );
}
